import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save, Loader2, Search, Copy } from "lucide-react";
import { adminHeaders, useAdminAuthed } from "./AdminLayout";
import { ImageField } from "./MediaUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A generic admin CRUD screen driven by a field schema. Every existing sidebar
 * module (FAQs, Botanicals, Ingredients, Categories, Reviews, Coupons, Blog,
 * Influencers, Media) can be rendered by pointing this at an endpoint and
 * declaring its columns + editable fields.
 *
 * Props:
 *   endpoint     REST base, e.g. "/admin/faqs"
 *   labelSingular "FAQ"
 *   fields[]      [{ name, label, kind, options?, help? }]
 *                 kind: text | textarea | number | boolean | select | tags | image | json
 *   columns[]     [{ name, label, render?(v,row) }]
 *   defaults      shape of a new item
 *   searchKeys[]  fields we filter with the top search bar
 *   testIdPrefix  data-testid namespace
 */
const AdminCRUD = ({
  endpoint,
  labelSingular = "Item",
  fields = [],
  columns = [],
  defaults = {},
  searchKeys = [],
  testIdPrefix = "crud",
  extraActions,
}) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {...row}
  const [saving, setSaving] = useState(false);
  const pw = useAdminAuthed();

  const load = async () => {
    if (!pw) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}${endpoint}`, { headers: adminHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(`Failed to load ${labelSingular.toLowerCase()}s`);
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint, pw]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (searchKeys.length ? searchKeys : Object.keys(r))
        .some((k) => String(r[k] ?? "").toLowerCase().includes(s))
    );
  }, [rows, q, searchKeys]);

  const onAdd = () => setEditing({ ...defaults, __isNew: true });
  const onEdit = (row) => setEditing({ ...row });
  const onDup = (row) => setEditing({ ...row, id: undefined, __isNew: true });
  const onDelete = async (row) => {
    if (!window.confirm(`Delete this ${labelSingular.toLowerCase()}?`)) return;
    try {
      await axios.delete(`${API}${endpoint}/${row.id}`, { headers: adminHeaders() });
      toast.success(`${labelSingular} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to delete`);
    }
  };

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.__isNew;
      delete body.id;
      delete body.created_at;
      delete body.updated_at;
      if (editing.__isNew) {
        await axios.post(`${API}${endpoint}`, body, { headers: adminHeaders() });
        toast.success(`${labelSingular} added`);
      } else {
        await axios.put(`${API}${endpoint}/${editing.id}`, body, { headers: adminHeaders() });
        toast.success(`${labelSingular} updated`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-root`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-2)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${labelSingular.toLowerCase()}s…`}
            className="w-full h-10 pl-9 pr-3 rounded-md border border-[var(--hk-border)] bg-white/70 text-[13px]"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
        {extraActions}
        <button
          onClick={onAdd}
          className="hk-btn-primary h-10 px-4"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus size={14} /> Add {labelSingular}
        </button>
      </div>

      <div className="hk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
              <tr>
                {columns.map((c) => (
                  <th key={c.name} className="text-left px-4 py-3 font-medium">{c.label}</th>
                ))}
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[var(--hk-ink-2)]">
                  <Loader2 className="inline animate-spin" size={16} /> Loading…
                </td></tr>
              )}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[var(--hk-ink-2)]">
                  No {labelSingular.toLowerCase()}s yet — click <em>Add {labelSingular}</em> to create one.
                </td></tr>
              )}
              {!busy && filtered.map((r) => (
                <tr key={r.id} className="border-t border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]/50">
                  {columns.map((c) => (
                    <td key={c.name} className="px-4 py-3 align-top">
                      {c.render ? c.render(r[c.name], r) : String(r[c.name] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => onDup(r)} className="text-[var(--hk-ink-2)] hover:text-[var(--hk-ink)] p-1.5" title="Duplicate" data-testid={`${testIdPrefix}-dup-${r.id}`}><Copy size={14} /></button>
                    <button onClick={() => onEdit(r)} className="text-[var(--hk-ink-2)] hover:text-[var(--hk-ink)] p-1.5 ml-1" title="Edit" data-testid={`${testIdPrefix}-edit-${r.id}`}><Pencil size={14} /></button>
                    <button onClick={() => onDelete(r)} className="text-red-600 hover:text-red-700 p-1.5 ml-1" title="Delete" data-testid={`${testIdPrefix}-del-${r.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <div className="bg-[var(--hk-ivory)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-modal`}>
            <div className="px-6 py-4 border-b border-[var(--hk-border)] flex items-center">
              <h3 className="hk-h4">{editing.__isNew ? `New ${labelSingular}` : `Edit ${labelSingular}`}</h3>
              <button className="ml-auto p-1.5 text-[var(--hk-ink-2)]" onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              {fields.map((f) => (
                <FieldRow
                  key={f.name}
                  field={f}
                  value={editing[f.name]}
                  onChange={(v) => setEditing((e) => ({ ...e, [f.name]: v }))}
                  testId={`${testIdPrefix}-field-${f.name}`}
                />
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[var(--hk-border)] flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="hk-btn-secondary h-10 px-4">Cancel</button>
              <button onClick={onSave} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid={`${testIdPrefix}-save`}>
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FieldRow = ({ field, value, onChange, testId }) => {
  const label = (
    <label className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
      {field.label}
      {field.required && <span className="text-red-600"> *</span>}
    </label>
  );
  const inputCls = "mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] focus:outline-none focus:border-[var(--hk-ink)]";
  switch (field.kind) {
    case "textarea":
      return (
        <div>{label}
          <textarea value={value ?? ""} rows={field.rows || 4} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
          {field.help && <p className="text-xs text-[var(--hk-ink-2)] mt-1">{field.help}</p>}
        </div>
      );
    case "number":
      return (
        <div>{label}
          <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={inputCls} data-testid={testId} />
          {field.help && <p className="text-xs text-[var(--hk-ink-2)] mt-1">{field.help}</p>}
        </div>
      );
    case "boolean":
      return (
        <label className="flex items-center gap-3 py-1 cursor-pointer" data-testid={testId}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
          <span className="text-[13px] text-[var(--hk-ink)]">{field.label}</span>
          {field.help && <span className="text-xs text-[var(--hk-ink-2)]">— {field.help}</span>}
        </label>
      );
    case "select":
      return (
        <div>{label}
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
            {(field.options || []).map((o) =>
              typeof o === "string"
                ? <option key={o} value={o}>{o}</option>
                : <option key={o.value} value={o.value}>{o.label}</option>
            )}
          </select>
        </div>
      );
    case "tags":
      return (
        <div>{label}
          <input
            value={Array.isArray(value) ? value.join(", ") : (value || "")}
            onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="comma, separated, values"
            className={inputCls}
            data-testid={testId}
          />
        </div>
      );
    case "image":
      return (
        <div>{label}
          <div className="mt-1.5">
            <ImageField value={value ?? ""} onChange={onChange} testid={testId} aspect="wide" help={field.help} />
          </div>
        </div>
      );
    default:
      return (
        <div>{label}
          <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
          {field.help && <p className="text-xs text-[var(--hk-ink-2)] mt-1">{field.help}</p>}
        </div>
      );
  }
};

export default AdminCRUD;
